import Vue from 'vue'
import Vuex from 'vuex'
// Plugins
import createPersistedState from 'vuex-persistedstate'
// Modules
import configurator from './modules/configurator'
import distributor from './modules/distributor'
import employees from './modules/employees'
import vacationTickets from './modules/vacationTickets'
import purchaseDeliveries from './modules/purchaseDeliveries'
import clients from './modules/clients'
import internalTickets from './modules/internalTickets'
import inventory from './modules/inventory'
import serviceCalls from './modules/serviceCalls'
import userPreferences from './modules/userPreferences'
import drawingRequests from './modules/drawingRequests'
import quotes from './modules/quotes'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    currentUser: {},
    userRoles: [],
    userColorMap: {},
    //  Global Settings
    settings: {},
      // human_resources_admins       [id]  // deprecate
      // ancestry_root                 id
      // dev                           id
      // super_admins                 [id]
      // vacations_inbox             email: string // deprecate
      // usd_crc_rate                float
      // distribution_admins          [id]
      // enable_service_layer        boolean
      // developers                   [id]

    // UI Settings
    previousRoute: null,
    projectStatusFilter: ['baja', 'media', 'alta', 'licitación', 'vendido'],
    transactionStatusFilter: ['por_aprobar', 'pendiente', 'en_proceso', 'aprobada', 'pendiente_af', 'rechazada'], // Use in CardTransactionIndex.vue
    displayCRC: false
  },
  getters: {
    // General
    usdCrRate(state){
      return state.settings.usd_crc_rate
    },
    eurRate(state){
      return state.settings.eur_crc_rate
    },
    canRate(state){
      return state.settings.can_crc_rate
    },
    userColorById: (state) => (userId) => {
      return state.userColorMap[userId] || '#2253a3'
    },
    // Auth / From settings
    isSuperAdmin (state) {
      return state.settings.super_admins.includes(state.currentUser.id)
    },
    isDistributionAdmin (state) {
      return state.settings.distribution_admins.includes(state.currentUser.id)
    },
    isHrAdmin (state) {
      return state.settings.human_resources_admins.includes(state.currentUser.id)
    },
    isRoot (state) {
      return id => state.settings.ancestry_root === id
    },
    isDev (state) {
      return state.settings.developers.includes(state.currentUser.id)
    },
    // Auth / User.role
    isManager (state) {
      return state.currentUser.is_manager
    },
    isAdmin (state) {
      return state.currentUser.role === 'administrador'
    },
    isSupervisor (state) {
      let role = state.currentUser.role
      if (role === 'administrador' || role === 'supervisor_ventas' || role === 'supervisor_división' || role === 'gerente') {
        return true
      } else {
        return false
      }
    },
    isSalesSupervisor (state) {
      return state.currentUser.role === 'supervisor_ventas'
    },
    isDistributor (state) {
      return state.currentUser.role === 'distribuidor'
    },
    isInstaller (state) {
      return state.currentUser.role === 'instalador'
    },
    isProductLeader (state) {
      return state.currentUser.role === 'lider_producto'
    },
    isDivisionSupervisor (state) {
      return state.currentUser.role === 'supervisor_división'
    },
    canViewAccess (state) {
      return accessModel => {
        if (state.currentUser.role == 'supervisor_división' && !state.currentUser.permissions.includes(accessModel)) {
          return false
        } else {
          return true
        }
      }
    },
    // Auth / Static permissions -> migrate to user roles
    isClientAdmin(state) {
      const allowedUsers = {
        69: 'Carlos Giral'
      }
      const allowedUserIds = Object.keys(allowedUsers).map(Number)
      return allowedUserIds.includes(state.currentUser.id)
    },
    isSocAdmin (state) {
      return [123].includes(state.currentUser.id)
    },
    // Auth / User roles
    isCoordinator (state) {
      const matches = state.userRoles.filter(s => s.includes('Coordinador'))
      return matches.length > 0
    },
    isCoursesAdmin(state) {
      return state.userRoles.includes('Cursos - Administradores')
    },
    isCoursesMember(state) {
      return state.userRoles.includes('Cursos - Miembros')
    },
    isTransactionAssetAdmin(state) {
      return state.userRoles.includes('Tarjetas - Administrador AF')
    },
    isTransactionAdmin(state) {
      return state.userRoles.includes('Tarjetas - Administrador')
    },
    isAtkAdvisor (state) {
      const matches = state.userRoles.filter(role => role.includes('ATK - asesores'))
      return matches.length > 0
    },
    approvalRulesPermissions (state){
      return state.userRoles.filter(role => role.includes('Reglas de Aprobación -'))
    },
    isOsGestor (state) {
      return state.userRoles.includes('OS - Gestores')
    },
    isDrawingDrafter (state) {
      return state.userRoles.includes('Solicitud planos - Dibujantes')
    },
    isDrawingAdmin (state) {
      return state.userRoles.includes('Solicitud planos - Administradores')
    },
  },
  actions: {

  },
  mutations: {
    setDisplayCRC (state, boolean) {
      state.displayCRC = boolean
    },
    setProjectStatusFilter (state, array) {
      Vue.set(state, 'projectStatusFilter', array)
    },
    setTransactionStatusFilter (state, array) {
      Vue.set(state, 'transactionStatusFilter', array)
    },
    setCrcRates (state, rates) {
      state.settings.usd_crc_rate = rates.usd_crc_rate
      state.settings.eur_crc_rate = rates.eur_crc_rate
      state.settings.can_crc_rate = rates.can_crc_rate
    },
    toggleServiceLayer (state) {
      state.settings.enable_service_layer = !state.settings.enable_service_layer
    },
    initData (state, { currentUser, roles, settings }) {
      state.currentUser = currentUser
      state.userRoles = roles
      state.settings = settings
    },
    setUserColor (state, userId) {
      if (!userId || state.userColorMap[userId]) return '#2253a3'

      const min = 50
      const r = Math.floor(min + Math.random() * 130)
      const g = Math.floor(min + Math.random() * 140)
      const b = Math.floor(min + Math.random() * 80)
      Vue.set(state.userColorMap, userId, `rgb(${r}, ${g}, ${b})`)
    }
  },
  modules: {
    configurator,
    distributor,
    employees,
    vacationTickets,
    purchaseDeliveries,
    clients,
    internalTickets,
    inventory,
    serviceCalls,
    userPreferences,
    drawingRequests,
    quotes
  },
  plugins: [
    createPersistedState()
  ]
})
